import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type DocumentationSectionId =
  | 'overview'
  | 'platform-monitoring'
  | 'system-configuration'
  | 'security-audit';

interface DocumentationSection {
  id: DocumentationSectionId;
  title: string;
  subtitle: string;
}

interface DocumentationArticleSection {
  heading: string;
  paragraphs: string[];
}

interface DocumentationArticle {
  id: string;
  title: string;
  sectionId: DocumentationSectionId;
  sections: DocumentationArticleSection[];
  relatedArticleIds: string[];
}

const DOCUMENTATION_SECTIONS: DocumentationSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    subtitle: 'Platform overview, architecture summary',
  },
  {
    id: 'platform-monitoring',
    title: 'Platform Monitoring',
    subtitle: 'Logs, Jobs, Performance, Service Bus',
  },
  {
    id: 'system-configuration',
    title: 'System Configuration',
    subtitle: 'Settings, ITIL Config, Pipeline Defaults',
  },
  {
    id: 'security-audit',
    title: 'Security & Audit',
    subtitle: 'Sign-in logs, permissions, role system',
  },
];

const DOCUMENTATION_ARTICLES: DocumentationArticle[] = [
  {
    id: 'platform-overview',
    title: 'Platform Overview',
    sectionId: 'overview',
    sections: [
      {
        heading: 'Purpose',
        paragraphs: [
          'CloudIncidents is a multi-tenant incident management platform designed for managed service providers (MSPs) that support many customer environments at once.',
          'Every major workflow is tenant-aware, so admins can monitor service quality globally while still preserving customer isolation for incidents, configurations, and audit trails.',
        ],
      },
      {
        heading: 'Core Capabilities',
        paragraphs: [
          'The platform combines event ingestion, triage workflows, AI-assisted response guidance, and SLA-aware tracking so teams can move from detection to resolution quickly.',
          'Tenant operations teams use a dedicated portal, while the Admin Portal centralizes governance controls such as templates, platform monitoring, and role assignments.',
        ],
      },
      {
        heading: 'Operational Model',
        paragraphs: [
          'CloudIncidents is built to support both always-on monitoring and human-in-the-loop operations, allowing responders to intervene, document decisions, and communicate status with full traceability.',
          'As your customer base scales, centralized templates and automation defaults reduce drift while preserving enough flexibility for tenant-specific service agreements.',
        ],
      },
    ],
    relatedArticleIds: ['system-logs', 'scheduled-jobs', 'system-settings'],
  },
  {
    id: 'architecture-summary',
    title: 'Architecture Summary',
    sectionId: 'overview',
    sections: [
      {
        heading: 'Service Layout',
        paragraphs: [
          'The platform uses a service-oriented backend with dedicated modules for incident workflows, monitoring telemetry, authentication, and system configuration.',
          'Supporting services are exposed through secure API endpoints consumed by the admin and tenant-facing web applications.',
        ],
      },
      {
        heading: 'Data and Isolation',
        paragraphs: [
          'Tenant-level boundaries are enforced at the data and application layers, ensuring one tenant cannot access another tenant’s incidents, users, or policy artifacts.',
          'System-level metadata is maintained separately to support shared infrastructure management without violating customer isolation.',
        ],
      },
      {
        heading: 'Reliability and Change Management',
        paragraphs: [
          'Background jobs, health probes, and centralized logging provide visibility into the state of ingestion, processing, and outbound integrations.',
          'Version history and deployment metadata in the System Info page help admins correlate behavior changes with specific releases and build artifacts.',
        ],
      },
    ],
    relatedArticleIds: ['platform-overview', 'scheduled-jobs', 'security-permissions'],
  },
  {
    id: 'system-logs',
    title: 'System Logs',
    sectionId: 'platform-monitoring',
    sections: [
      {
        heading: 'Using the Logs Page',
        paragraphs: [
          'The Logs page is the first stop for investigating platform behavior. It provides structured entries with timestamp, severity, logger source, correlation ID, and contextual fields.',
          'Use search and filter controls together to quickly isolate a time window, subsystem, or tenant-related request path.',
        ],
      },
      {
        heading: 'Filtering by Level',
        paragraphs: [
          'Severity filters support Trace, Debug, Information, Warning, Error, and Critical. For routine triage, start with Warning and above, then broaden if needed.',
          'When troubleshooting a specific execution path, include Information entries and correlate by correlation ID to reconstruct request flow end-to-end.',
        ],
      },
      {
        heading: 'Export and Handoff',
        paragraphs: [
          'Use the export option to generate a CSV snapshot for incident postmortems, stakeholder updates, or escalation to engineering teams.',
          'Before sharing logs externally, verify that customer-identifying details are handled according to your organization’s retention and privacy standards.',
        ],
      },
    ],
    relatedArticleIds: ['scheduled-jobs', 'security-permissions', 'platform-overview'],
  },
  {
    id: 'scheduled-jobs',
    title: 'Scheduled Jobs',
    sectionId: 'platform-monitoring',
    sections: [
      {
        heading: 'What Background Jobs Do',
        paragraphs: [
          'Scheduled jobs run recurring platform tasks such as ingesting queued events, applying automation rules, housekeeping stale records, and dispatching outbound notifications.',
          'Queue-triggered and HTTP-triggered jobs are also surfaced so admins can verify both periodic and event-driven execution paths.',
        ],
      },
      {
        heading: 'Reading Job Health',
        paragraphs: [
          'Each job record exposes trigger type, schedule or queue source, last run, next run, and current status so you can quickly detect delays or failures.',
          'A healthy timer job typically shows consistent run cadence and predictable next execution; unexpected gaps can indicate upstream dependency issues.',
        ],
      },
      {
        heading: 'Operational Actions',
        paragraphs: [
          'Use manual refresh to validate that remediation actions have taken effect after a configuration change or transient outage.',
          'If a job repeatedly fails, capture relevant log correlation IDs and include schedule details in the incident to accelerate root-cause analysis.',
        ],
      },
    ],
    relatedArticleIds: ['system-logs', 'system-settings', 'architecture-summary'],
  },
  {
    id: 'system-settings',
    title: 'System Settings',
    sectionId: 'system-configuration',
    sections: [
      {
        heading: 'Configuration Scope',
        paragraphs: [
          'System Settings control global platform behavior including support contact details, timezone handling, maintenance mode, session policy, and upload constraints.',
          'Changes made here apply broadly, so treat updates as controlled operational changes rather than ad hoc edits.',
        ],
      },
      {
        heading: 'Email and Assistant Settings',
        paragraphs: [
          'SendGrid settings define outbound email credentials and sender identity used for notifications and lifecycle communications.',
          'Assistant settings define model defaults by service tier, with optional tenant overrides for cases requiring stricter cost or capability controls.',
        ],
      },
      {
        heading: 'Change Safety',
        paragraphs: [
          'Document why a setting changed, who approved it, and the expected impact window before applying modifications in production.',
          'After update, validate expected behavior by reviewing system logs, job health, and incident workflow activity to confirm no unintended side effects.',
        ],
      },
    ],
    relatedArticleIds: ['itil-configuration', 'security-permissions', 'platform-overview'],
  },
  {
    id: 'itil-configuration',
    title: 'ITIL Configuration',
    sectionId: 'system-configuration',
    sections: [
      {
        heading: 'Priority Templates',
        paragraphs: [
          'Priority templates define incident urgency and impact mappings used to calculate standardized priority values across tenants.',
          'Keep naming and scoring consistent so dashboards, SLA policies, and escalation paths remain predictable for support teams.',
        ],
      },
      {
        heading: 'Reason Templates',
        paragraphs: [
          'Reason templates define controlled vocabularies for resolution, on-hold, and cancellation outcomes.',
          'Using standardized reasons improves reporting quality and makes trend analysis far more reliable during service reviews.',
        ],
      },
      {
        heading: 'SLA Policy Templates',
        paragraphs: [
          'SLA policy templates map priority levels to response and resolution targets, with optional business-hours behavior for contractual compliance.',
          'When adjusting templates, evaluate the downstream effect on existing incidents and confirm updates align with contractual obligations.',
        ],
      },
    ],
    relatedArticleIds: ['system-settings', 'scheduled-jobs', 'security-permissions'],
  },
  {
    id: 'security-permissions',
    title: 'Security & Permissions',
    sectionId: 'security-audit',
    sections: [
      {
        heading: 'Role-Based Access Control',
        paragraphs: [
          'CloudIncidents uses role-based access control (RBAC) to grant capabilities according to operational responsibility.',
          'System-level roles control governance functions in the Admin Portal, while tenant-scoped roles govern day-to-day incident operations in customer contexts.',
        ],
      },
      {
        heading: 'Built-In Roles',
        paragraphs: [
          'Built-in roles are designed as secure baselines that can be audited and consistently applied across deployments.',
          'Use least-privilege principles when assigning elevated roles, and review assignments regularly to remove stale access.',
        ],
      },
      {
        heading: 'Audit and Sign-In Review',
        paragraphs: [
          'Use sign-in logs to verify who accessed the platform, from where, and whether authentication attempts succeeded or failed.',
          'For security events, combine sign-in evidence with system logs and role assignment history to build a complete timeline before remediation.',
        ],
      },
    ],
    relatedArticleIds: ['system-logs', 'system-settings', 'platform-overview'],
  },
];

const ARTICLE_BY_ID = new Map(DOCUMENTATION_ARTICLES.map((article) => [article.id, article]));

export function DocumentationPage() {
  const [searchText, setSearchText] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const routeArticleId = useMemo(() => {
    const basePath = '/documentation';
    if (!location.pathname.startsWith(basePath)) return '';
    const suffix = location.pathname.slice(basePath.length);
    const [firstSegment] = suffix.split('/').filter(Boolean);
    return firstSegment || '';
  }, [location.pathname]);

  const selectedArticle = ARTICLE_BY_ID.get(routeArticleId) ?? DOCUMENTATION_ARTICLES[0];
  const selectedSection = DOCUMENTATION_SECTIONS.find((section) => section.id === selectedArticle.sectionId) ?? DOCUMENTATION_SECTIONS[0];

  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredNavigation = useMemo(
    () =>
      DOCUMENTATION_SECTIONS.map((section) => ({
        ...section,
        articles: DOCUMENTATION_ARTICLES.filter(
          (article) =>
            article.sectionId === section.id &&
            (normalizedSearch.length === 0 || article.title.toLowerCase().includes(normalizedSearch)),
        ),
      })),
    [normalizedSearch],
  );

  const relatedArticles = selectedArticle.relatedArticleIds
    .map((id) => ARTICLE_BY_ID.get(id))
    .filter((article): article is DocumentationArticle => Boolean(article));

  const goToArticle = (articleId: string) => {
    void navigate(`/documentation/${articleId}`);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Documentation</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Internal operational guidance for system administrators.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="card h-fit">
          <div className="mb-4">
            <label htmlFor="documentation-search" className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
              Search articles
            </label>
            <input
              id="documentation-search"
              type="search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Filter by title..."
              className="input"
            />
          </div>

          <div className="mb-4 md:hidden">
            <label htmlFor="mobile-article-select" className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
              Jump to article
            </label>
            <select
              id="mobile-article-select"
              className="input"
              value={selectedArticle.id}
              onChange={(event) => goToArticle(event.target.value)}
            >
              {filteredNavigation.map((section) => (
                <optgroup key={section.id} label={section.title}>
                  {section.articles.map((article) => (
                    <option key={article.id} value={article.id}>
                      {article.title}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <nav aria-label="Documentation navigation" className="hidden space-y-4 md:block">
            {filteredNavigation.map((section) => (
              <section key={section.id}>
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{section.title}</h2>
                <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{section.subtitle}</p>
                {section.articles.length > 0 ? (
                  <ul className="space-y-1">
                    {section.articles.map((article) => {
                      const isActive = article.id === selectedArticle.id;
                      return (
                        <li key={article.id}>
                          <button
                            type="button"
                            onClick={() => goToArticle(article.id)}
                            className={`w-full rounded px-2 py-2 text-left text-sm ${
                              isActive
                                ? 'bg-primary-700 text-white'
                                : 'text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                            }`}
                            aria-current={isActive ? 'page' : undefined}
                          >
                            {article.title}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">No matching articles.</p>
                )}
              </section>
            ))}
          </nav>
        </aside>

        <main className="card">
          <nav aria-label="Breadcrumb" className="mb-4">
            <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <li>Documentation</li>
              <li aria-hidden="true">/</li>
              <li>{selectedSection.title}</li>
              <li aria-hidden="true">/</li>
              <li className="font-medium text-gray-900 dark:text-gray-100">{selectedArticle.title}</li>
            </ol>
          </nav>

          <article>
            <h2 className="mb-6 text-2xl font-semibold text-gray-900 dark:text-gray-100">{selectedArticle.title}</h2>
            <div className="space-y-8">
              {selectedArticle.sections.map((section) => (
                <section key={section.heading}>
                  <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">{section.heading}</h3>
                  <div className="space-y-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 ? (
            <section className="mt-8 border-t border-gray-200 pt-6 dark:border-gray-700">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                Related articles
              </h3>
              <ul className="flex flex-wrap gap-2">
                {relatedArticles.map((article) => (
                  <li key={article.id}>
                    <button
                      type="button"
                      onClick={() => goToArticle(article.id)}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      {article.title}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </main>
      </div>
    </div>
  );
}
